/**
 * Cliente de Supabase.
 *
 * Solo se crea si hay credenciales; en modo demo nunca llega a instanciarse.
 * Se usa exclusivamente la clave anonima (RNF-06): la clave de servicio vive
 * en las Edge Functions y jamas viaja al navegador.
 */

import {
  FunctionsFetchError,
  FunctionsHttpError,
  FunctionsRelayError,
  createClient,
  type SupabaseClient,
} from '@supabase/supabase-js';

import { env } from '@/config/env';

import { RepositoryError } from '../types';

let client: SupabaseClient | null = null;

export function getSupabase(): SupabaseClient {
  if (!client) {
    if (!env.supabaseUrl || !env.supabaseAnonKey) {
      throw new Error(
        'Faltan VITE_SUPABASE_URL o VITE_SUPABASE_ANON_KEY. Copia .env.example a .env.',
      );
    }
    client = createClient(env.supabaseUrl, env.supabaseAnonKey, {
      auth: {
        // La sesion persiste entre visitas con refresco rotatorio (RF-AUTH-08).
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
        storageKey: 'cp.auth',
        flowType: 'pkce',
      },
      // Sin cabeceras propias. Aqui iba `x-application-name`, que no aporta
      // nada funcional y que las Edge Functions no tienen en su lista CORS: el
      // navegador bloqueaba la llamada en la comprobacion previa y fallaba
      // TODA funcion invocada desde la app —enviar un video, aprobar, rechazar,
      // publicar resultados—, siempre con "Algo ha salido mal".
    });
  }
  return client;
}

/** Invoca una Edge Function, que es donde vive la logica con privilegios. */
export async function invokeFunction<T>(
  name: string,
  body?: Record<string, unknown>,
): Promise<T> {
  const { data, error } = await getSupabase().functions.invoke<T>(name, { body });
  if (!error) return data as T;

  // La funcion respondio con error: su cuerpo trae el codigo que la interfaz
  // sabe traducir (`video.NOT_PUBLIC`, `submission.NOT_ENROLLED`...). Antes se
  // relanzaba el error de supabase-js tal cual, sin leer el cuerpo, y la
  // interfaz solo traduce RepositoryError: cualquier rechazo del servidor
  // salia como "Algo ha salido mal".
  if (error instanceof FunctionsHttpError) {
    const payload = await readJson(error.context);
    const code = typeof payload?.error === 'string' ? payload.error : 'generic';
    throw new RepositoryError(code, payload ?? {});
  }

  // La peticion no llego: sin red, o bloqueada por el navegador (CORS). Se deja
  // el detalle en la consola, que es donde se puede diagnosticar.
  if (error instanceof FunctionsFetchError || error instanceof FunctionsRelayError) {
    console.error(`[${name}] la llamada no llego a la funcion`, error);
    const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
    throw new RepositoryError(offline ? 'network' : 'generic', { detail: error.message });
  }

  throw error;
}

async function readJson(response: unknown): Promise<Record<string, unknown> | null> {
  try {
    if (response instanceof Response) return (await response.clone().json()) as Record<string, unknown>;
  } catch {
    // Cuerpo que no es JSON: una caida de la funcion, por ejemplo.
  }
  return null;
}
